import type { ModelScorecardScenarioSet } from '@realplc/types';
import { apiFetch } from './auth';

export interface AdminDataSource {
  kind: 'database' | 'json_store' | 'derived' | 'estimated' | 'mixed' | 'none';
  label: string;
  detail: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface AiProviderKey {
  id: string;
  name: string;
  keyFingerprint: string;
  status: string;
  maxConcurrency: number;
  rpmLimit?: number | null;
  dailyLimit?: number | null;
  currentConcurrency: number;
  successCount: number;
  failureCount: number;
  consecutiveFailures: number;
  avgLatencyMs?: number | null;
  cooldownUntil?: number | null;
  lastUsedAt?: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface AiProviderPool {
  id: string;
  name: string;
  provider: string;
  baseUrl?: string | null;
  status: string;
  priority: number;
  createdAt: number;
  updatedAt: number;
  keys: AiProviderKey[];
}

export interface AdminDashboardData {
  dataSources: Record<string, AdminDataSource>;
  users: Array<{
    id: string;
    email?: string;
    name?: string;
    displayName: string;
    identityProvider?: string;
    status: string;
    lastLoginAt?: number | null;
    createdAt: number;
  }>;
  organizations: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    planType: string;
    ownerId: string;
    ownerName: string;
    ownerEmail?: string | null;
    createdAt: number;
    updatedAt: number;
  }>;
  projects: Array<{
    id: string;
    organizationId: string;
    name: string;
    description?: string | null;
    vendorType?: string | null;
    visibility: string;
    createdBy: string;
    createdByName: string;
    organizationName: string;
    createdAt: number;
    updatedAt: number;
  }>;
  invites: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    email: string;
    role: string;
    status: string;
    expiresAt: number;
    createdBy: string;
    createdByName: string;
    createdAt: number;
  }>;
  apiKeys: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    name: string;
    status: string;
    expiresAt?: number | null;
    lastUsedAt?: number | null;
    createdBy: string;
    createdByName: string;
    createdAt: number;
  }>;
  serviceAccounts: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    name: string;
    clientId: string;
    status: string;
    lastUsedAt?: number | null;
    createdBy: string;
    createdByName: string;
    createdAt: number;
    updatedAt: number;
  }>;
  deployments: Array<{
    id: string;
    projectId: string;
    projectName: string;
    organizationId: string;
    organizationName: string;
    artifactId?: string | null;
    requestedBy: string;
    requestedByName: string;
    approvedBy?: string | null;
    approvedByName?: string | null;
    executedBy?: string | null;
    executedByName?: string | null;
    status: string;
    notes?: string | null;
    createdAt: number;
    updatedAt: number;
    approvedAt?: number | null;
    executedAt?: number | null;
  }>;
  auditLogs: Array<{
    traceId?: string;
    actorUserId?: string;
    organizationId?: string;
    projectId?: string;
    action: string;
    resourceType?: string;
    resourceId?: string;
    result: string;
    detail?: Record<string, unknown>;
    createdAt?: number;
    actorLabel?: string | null;
    organizationName?: string | null;
    projectName?: string | null;
  }>;
  conversationOverview: {
    userCount: number;
    sessionCount: number;
    messageCount: number;
    estimatedPromptTokens: number;
    estimatedCompletionTokens: number;
    estimatedTotalTokens: number;
    actualPromptTokens: number | null;
    actualCompletionTokens: number | null;
    actualTotalTokens: number | null;
    compositePromptTokens?: number | null;
    compositeCompletionTokens?: number | null;
    compositeTotalTokens?: number | null;
    sessionsWithTelemetry: number;
    sessionsMissingTimestamps: number;
    sessionsWithActualTokens: number;
    sessionsMissingActual: number;
    lastActivityAt?: number | null;
  };
  conversations: Array<{
    userId: string;
    userName?: string | null;
    userEmail?: string | null;
    userLabel: string;
    source: 'database' | 'json_store';
    sessionCount: number;
    messageCount: number;
    estimatedPromptTokens: number;
    estimatedCompletionTokens: number;
    estimatedTotalTokens: number;
    actualPromptTokens: number | null;
    actualCompletionTokens: number | null;
    actualTotalTokens: number | null;
    compositePromptTokens?: number | null;
    compositeCompletionTokens?: number | null;
    compositeTotalTokens?: number | null;
    usageSource?: string | null;
    lastActivityAt: number;
    sessionsWithTelemetry: number;
    sessionsMissingTimestamps: number;
    sessions: Array<{
      id: string;
      title: string;
      status?: string | null;
      updatedAt: number;
      deletedAt?: number | null;
      isDeleted?: boolean;
      startedAt: number;
      lastMessageAt: number;
      messageCount: number;
      userMessageCount: number;
      assistantMessageCount: number;
      estimatedPromptTokens: number;
      estimatedCompletionTokens: number;
      estimatedTotalTokens: number;
      actualPromptTokens: number | null;
      actualCompletionTokens: number | null;
      actualTotalTokens: number | null;
      compositePromptTokens?: number | null;
      compositeCompletionTokens?: number | null;
      compositeTotalTokens?: number | null;
      usageSource?: string | null;
      messageTimestampCoverage: 'full' | 'partial' | 'none';
      missingMessageTimestampCount: number;
      hasTelemetry: boolean;
      hasReasoning: boolean;
      hasStructuredOutput: boolean;
      preview: string;
      lastProvider?: string | null;
      lastModel?: string | null;
      primaryProvider?: string | null;
      primaryModel?: string | null;
      routingModes: string[];
      isStreaming: boolean;
      sessionConfig?: Record<string, unknown> | null;
      messages: Array<{
        id: string;
        role: string;
        content: string;
        contentPreview: string;
        reasoning?: string;
        timestamp?: number | null;
        timestampSource: 'message' | 'session_updated_at' | 'missing';
        estimatedTokens: number;
        tokenSource: 'model_attempt_estimate' | 'content_estimate';
        actualPromptTokens: number | null;
        actualCompletionTokens: number | null;
        actualTotalTokens: number | null;
        compositePromptTokens?: number | null;
        compositeCompletionTokens?: number | null;
        compositeTotalTokens?: number | null;
        usageSource?: string | null;
        provider?: string | null;
        model?: string | null;
        routingMode?: string | null;
        deliveryQuality?: import('../types').GenerationMetrics['deliveryQuality'];
        physicalRequestStarts?: number | null;
        candidateAttemptRecords?: number | null;
        requestAccounting?: import('../types').GenerationMetrics['requestAccounting'] | null;
        requestStop?: import('../types').GenerationMetrics['requestStop'] | null;
        traceId?: string | null;
        requestId?: string | null;
        clientFirstTokenMs?: number | null;
        totalMs?: number | null;
        queueWaitMs?: number | null;
        modelLatencyMs?: number | null;
        modelAttempts?: Array<{
          attemptId?: string;
          stageId?: string;
          parentAttemptId?: string;
          firstBodyMs?: number;
          runtime?: import('../types').GenerationModelAttempt['runtime'];
          keyAttempts?: import('../types').GenerationModelAttempt['keyAttempts'];
          errorDetail?: string;
          failureScope?: import('../types').GenerationModelAttempt['failureScope'];
          stage: string;
          route: string;
          provider: string;
          model: string;
          status: string;
          attempt: number;
          totalCandidates: number;
          poolSize: number;
          credentialedPoolSize: number;
          latencyMs: number;
          startedAt: number;
          endedAt: number;
          /** How many attempts this record stands for, where a run of the same failure was stored folded. */
          repeats?: number;
          outputChars?: number;
          tokensEstimate?: number;
          finishReason?: string;
          truncated?: boolean;
          actualPromptTokens?: number;
          actualCompletionTokens?: number;
          actualTotalTokens?: number;
          cachedInputTokens?: number;
          reasoningTokens?: number;
          timedOut?: boolean;
          httpStatus?: number;
          errorCategory?: string;
          errorMessage?: string;
        }> | null;
        diagnosticEvents?: Array<{
          level: 'info' | 'warning' | 'error';
          stage: string;
          message: string;
          code?: string;
          at: number;
          userVisible?: boolean;
          recoverable?: boolean;
        }>;
        /** The cards the turn asked, and the answers given on them (stage 4.2). Null where it asked none. */
        clarificationChecklist?: Array<{
          id: string;
          question: string;
          category?: string;
          options?: string[];
          multiSelect?: boolean;
          required?: boolean;
          defaultValue?: string;
          fieldKeys?: string[];
        }> | null;
        clarifyAnswers?: Array<{
          id: string;
          question?: string;
          category?: string;
          selectedOptions: string[];
          fieldKeys?: string[];
        }> | null;
        hasTelemetry: boolean;
        hasReasoning: boolean;
        hasPipeline: boolean;
        hasStructuredOutput: boolean;
        repairAppendix?: Record<string, unknown> | null;
        hasRepairAppendix?: boolean;
      }>;
    }>;
  }>;
  tokenUsage: Array<{
    userId: string;
    userLabel: string;
    userEmail?: string | null;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    actualPromptTokens: number | null;
    actualCompletionTokens: number | null;
    actualTotalTokens: number | null;
    compositePromptTokens?: number | null;
    compositeCompletionTokens?: number | null;
    compositeTotalTokens?: number | null;
    source: 'composite_usage' | 'actual_from_api' | 'estimated_from_messages';
  }>;
}

export interface QuotaDefaults {
  monthlyTokens: number;
}

export interface OrgQuotaEntry {
  orgId: string;
  name: string;
  slug: string;
  planType: string;
  ownerEmail?: string | null;
  ownerName?: string | null;
  monthlyTokens: number;
  usedTokens: number;
  resetAt: number;
  isOverridden: boolean;
  rollingWindow?: TenantQuotaRollingWindow;
  globalDefault: number;
}

export interface AdminQuotaData {
  defaults: QuotaDefaults | null;
  organizations: OrgQuotaEntry[];
}

export type ExportJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ExportJob {
  jobId: string;
  status: ExportJobStatus;
  progress: number;
  error?: string;
  downloadedAt?: number;
}

export interface AiTelemetryData {
  sampledResponses: number;
  firstPacketBreakdown: Array<{
    segment: string;
    sampleCount: number;
    p50Ms: number | null;
    p90Ms: number | null;
    avgMs: number | null;
  }>;
  selectedModelTable: Array<{
    provider: string;
    model: string;
    sampleCount: number;
    fastCount: number;
    thinkingCount: number;
    firstTokenP50Ms: number | null;
    totalP50Ms: number | null;
    queueP50Ms: number | null;
    modelLatencyP50Ms: number | null;
    dominantSlowStage: string;
  }>;
  attemptTable: Array<{
    provider: string;
    model: string;
    attemptCount: number;
    successCount: number;
    errorCount: number;
    timeoutCount: number;
    errorRate: number | null;
    timeoutRate: number | null;
    successLatencyP50Ms: number | null;
  }>;
  optimizationCandidates: Array<{
    provider: string;
    model: string;
    action: 'remove' | 'deprioritize';
    reason: string;
    attemptCount: number;
    errorRate: number | null;
    timeoutRate: number | null;
    successLatencyP50Ms: number | null;
  }>;
  recentRuns: Array<{
    userId: string;
    sessionId: string;
    sessionTitle: string;
    updatedAt: number;
    routingMode: string;
    provider: string;
    model: string;
    traceId?: string;
    requestId?: string;
    clientFirstTokenMs: number | null;
    totalMs: number | null;
    dominantStage: string;
  }>;
}

export async function loadAdminDashboard(): Promise<AdminDashboardData> {
  const response = await apiFetch(`${API_BASE}/api/admin/dashboard`);

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    throw new Error('Invalid response format - expected JSON');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to load admin dashboard');
  }

  return data as AdminDashboardData;
}

// ── Admin console: overview / sessions / audit ─────────────────────────────
// Lists carry summaries only; a message body is fetched one at a time when
// the administrator expands a turn. These replace what the console used to
// read from the corpus-loading /dashboard payload.

export type AdminSessionMessage = AdminDashboardData['conversations'][number]['sessions'][number]['messages'][number];
export type AdminAuditLogEntry = AdminDashboardData['auditLogs'][number];
export type AdminOverviewWindow = '7d' | '30d' | '90d';

export interface AdminSessionHeader {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  organizationId: string | null;
  projectId: string | null;
  title: string;
  status: string | null;
  isStreaming: boolean;
  lastProvider: string | null;
  lastModel: string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface AdminSessionRow extends AdminSessionHeader {
  turns: number;
  failedTurns: number;
  activeTurns: number;
  /** Turns that filed an alarm (TURN_ALARM_* in @realplc/types). Absent from a server before v3.2.3. */
  alarmTurns?: number;
  totalTokens: number;
  firstRunAt: number | null;
  lastRunAt: number | null;
  maxTurnMs: number | null;
}

export interface AdminSessionListQuery {
  start?: string;
  end?: string;
  userId?: string;
  q?: string;
  model?: string;
  hasFailure?: 'true';
  longTurnMs?: number;
  streaming?: 'true';
  includeDeleted?: 'true' | 'false';
  sortBy?: 'updatedAt' | 'turns' | 'tokens';
  sort?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

export interface AdminSessionListResult {
  sessions: AdminSessionRow[];
  pagination: { offset: number; limit: number; total: number };
  dateRange: { startMs: number; endMs: number };
}

/** Whether the PLC software opened a turn's project (owner, 2026-10-21); the gateway's plcOpen.ts says how each is read. */
export type AdminPlcOutcome = 'success' | 'failed' | 'not_attempted';
export interface AdminPlcOpenResult { outcome: AdminPlcOutcome; reason?: string; detail?: string }
export interface AdminTurnPlcOpen {
  codesys?: { load?: AdminPlcOpenResult; desktop?: AdminPlcOpenResult };
  tia?: { load?: AdminPlcOpenResult };
}
export interface AdminPlcOpenCounts { success: number; failed: number; notAttempted: number; rate: number | null }
export interface AdminPlcOpenReasonCount { engine: 'codesys' | 'tia'; layer: 'load' | 'desktop'; reason: string; count: number; sample?: string }
export interface AdminPlcOpenStats {
  codesys: { load: AdminPlcOpenCounts; desktop: AdminPlcOpenCounts };
  tia: { load: AdminPlcOpenCounts };
  topFailures: AdminPlcOpenReasonCount[];
  topNotAttempted: AdminPlcOpenReasonCount[];
}

export interface AdminSessionMessageIndexEntry {
  id: string;
  role: string;
  status: string | null;
  requestId: string | null;
  traceId: string | null;
  routingMode: string | null;
  timestamp: number | null;
  preview: string;
  totalTokens: number | null;
  usageSource: string | null;
  hasTelemetry: boolean;
  hasRepairAppendix: boolean;
  hasPipeline: boolean;
  hasPlanReview: boolean;
  totalMs: number | null;
  modelLatencyMs: number | null;
  queueWaitMs: number | null;
  modelAttemptCount: number | null;
  /** How the turn's plan review ended (stage 4.3), where it had one that ended. Absent from a gateway before it. */
  planReviewResult?: 'approved' | 'rejected' | 'modified' | 'regenerate_subtask' | 'timed_out' | null;
  /** Who approved it: the engineer, or the countdown. */
  planApprovalSource?: 'manual' | 'auto' | null;
  /** How long the turn waited on the engineer's approval, part of `totalMs`. */
  planReviewWaitMs?: number | null;
  /**
   * The models the turn called, merged by model (stage 4.1). Null where the turn has no record of its calls; empty
   * where it has one and called none.
   */
  modelCalls?: Array<{ provider: string; model: string; succeeded: number; failed: number; latencyMs: number }> | null;
  /** Whether the PLC software loaded the turn's project, and opened it on the desktop; null where the turn recorded nothing. */
  plcOpen?: AdminTurnPlcOpen | null;
}

export interface AdminSessionRun {
  requestId: string;
  traceId: string | null;
  userMessageId: string;
  assistantMessageId: string;
  status: string;
  provider: string | null;
  model: string | null;
  usageSource: string | null;
  errorMessage: string | null;
  attemptCount: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  realplcOutputTokens: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface AdminSessionDetail {
  session: AdminSessionHeader;
  messages: AdminSessionMessageIndexEntry[];
  runs: AdminSessionRun[];
}

export interface AdminRecentList<T> {
  items: T[];
  total: number;
}

export interface AdminOverviewData {
  window: { key: AdminOverviewWindow; startMs: number; endMs: number };
  totals: { users: number; organizations: number; projects: number; deployments: number; sessions: number; messages: number };
  activity: {
    turns: number;
    failedTurns: number;
    longTurns: number;
    activeSessions: number;
    activeUsers: number;
    totalTokens: number;
    lastActivityAt: number | null;
    longTurnThresholdMs: number;
  };
  attention: {
    failedTurns: number;
    longTurns: number;
    stuckStreamingSessions: number;
    providerFailures: Array<{ provider: string; count: number }>;
  };
  /** Absent from a server older than the PLC-open view. */
  plcOpen?: AdminPlcOpenStats;
  recentSessions: Array<{
    id: string;
    title: string;
    userId: string;
    userEmail: string | null;
    userLabel: string;
    lastProvider: string | null;
    lastModel: string | null;
    updatedAt: number;
    deletedAt: number | null;
    isStreaming: boolean;
  }>;
  recent: {
    organizations: AdminRecentList<AdminDashboardData['organizations'][number]>;
    projects: AdminRecentList<AdminDashboardData['projects'][number]>;
    invites: AdminRecentList<AdminDashboardData['invites'][number]>;
    apiKeys: AdminRecentList<AdminDashboardData['apiKeys'][number]>;
    serviceAccounts: AdminRecentList<AdminDashboardData['serviceAccounts'][number]>;
    deployments: AdminRecentList<AdminDashboardData['deployments'][number]>;
  };
  dataSources: Record<string, AdminDataSource>;
}

export interface AdminAuditLogPage {
  logs: AdminAuditLogEntry[];
  pagination: { offset: number; limit: number; hasMore: boolean };
}

async function readAdminJson<T>(response: Response, fallback: string): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { message?: string; error?: string }).message || (data as { error?: string }).error || fallback);
  }
  return data as T;
}

function toQueryString(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  return params.toString();
}

export async function loadAdminOverview(window: AdminOverviewWindow = '30d'): Promise<AdminOverviewData> {
  const response = await apiFetch(`${API_BASE}/api/admin/overview?window=${encodeURIComponent(window)}`);
  return readAdminJson(response, 'Failed to load overview');
}

export async function listAdminSessions(query: AdminSessionListQuery = {}): Promise<AdminSessionListResult> {
  const response = await apiFetch(`${API_BASE}/api/admin/sessions?${toQueryString(query as Record<string, string | number | undefined>)}`);
  return readAdminJson(response, 'Failed to load sessions');
}

export async function getAdminSession(sessionId: string): Promise<AdminSessionDetail> {
  const response = await apiFetch(`${API_BASE}/api/admin/sessions/${encodeURIComponent(sessionId)}`);
  return readAdminJson(response, 'Failed to load session');
}

export async function getAdminSessionMessage(sessionId: string, messageId: string): Promise<{ message: AdminSessionMessage }> {
  const response = await apiFetch(
    `${API_BASE}/api/admin/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}`,
  );
  return readAdminJson(response, 'Failed to load message');
}

export async function listAdminAuditLogs(
  query: { offset?: number; limit?: number; action?: string; actorUserId?: string; start?: string; end?: string } = {},
): Promise<AdminAuditLogPage> {
  const response = await apiFetch(`${API_BASE}/api/admin/audit-logs?${toQueryString(query)}`);
  return readAdminJson(response, 'Failed to load audit logs');
}

export async function loadAiTelemetry(): Promise<AiTelemetryData> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-telemetry`);

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    throw new Error('Invalid response format - expected JSON');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to load AI telemetry');
  }

  return data as AiTelemetryData;
}

export async function triggerConversationsExport(range?: { from?: string; to?: string; month?: string }): Promise<{
  jobId: string;
  status: ExportJobStatus;
}> {
  const response = await apiFetch(`${API_BASE}/api/admin/export/conversations`, {
    method: 'POST',
    headers: range ? { 'Content-Type': 'application/json' } : undefined,
    body: range ? JSON.stringify(range) : undefined,
  });

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    throw new Error('Invalid response format - expected JSON');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to trigger export');
  }
  return data as { jobId: string; status: ExportJobStatus };
}

export async function purgeConversations(input: {
  from?: string;
  to?: string;
  month?: string;
  backupJobId: string;
}): Promise<{ ok: boolean; deleted: Record<string, number> }> {
  const response = await apiFetch(`${API_BASE}/api/admin/conversations/purge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to clear conversations');
  }
  return data as { ok: boolean; deleted: Record<string, number> };
}

export async function deleteAdminUser(userId: string, confirm: 'Delete'): Promise<{
  ok: boolean;
  deleted: Record<string, number>;
  keycloak?: { deleted: boolean; warning?: string };
}> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to delete user');
  }
  return data as { ok: boolean; deleted: Record<string, number>; keycloak?: { deleted: boolean; warning?: string } };
}

export async function getConversationsExportStatus(jobId: string): Promise<ExportJob> {
  const response = await apiFetch(`${API_BASE}/api/admin/export/conversations/status/${jobId}`);

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    throw new Error('Invalid response format - expected JSON');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to get export status');
  }
  return data as ExportJob;
}

export async function downloadConversationsExport(jobId: string): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/admin/export/conversations/download/${jobId}`);

  if (!response.ok) {
    let message = 'Failed to download export';
    try {
      const payload = await response.json();
      message = payload.error || message;
    } catch {
      // ignore malformed error payload
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
  const fileName = matchedFileName || `conversations-export-${jobId}.zip`;
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export async function loadAdminQuota(): Promise<AdminQuotaData> {
  const response = await apiFetch(`${API_BASE}/api/admin/quota`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load quota data');
  return data as AdminQuotaData;
}

export async function saveGlobalQuotaDefaults(monthlyTokens: number): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/admin/quota/defaults`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monthlyTokens }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to save global defaults');
  }
}

export async function saveOrgQuotaOverride(
  orgId: string,
  monthlyTokens: number | null,
  patch: { planType?: string; usedTokens?: number; resetAt?: number } = {},
): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/admin/quota/organization/${orgId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monthlyTokens, ...patch }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to save org quota');
  }
}

export async function recalculateOrgQuota(orgId: string): Promise<{ orgId: string; usedTokens: number; resetAt: number }> {
  const response = await apiFetch(`${API_BASE}/api/admin/quota/organization/${orgId}/recalculate`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to recalculate org quota');
  }
  return data as { orgId: string; usedTokens: number; resetAt: number };
}

export interface TenantQuotaInfo {
  plan: string;
  monthlyTokens: number;
  usedTokens: number;
  resetAt: number;
  rollingWindow?: TenantQuotaRollingWindow;
}

export interface TenantQuotaRollingWindow {
  windowSeconds: number;
  maxTokens: number;
  usedTokens: number;
  resetAt: number | null;
}

export async function loadTenantQuota(): Promise<TenantQuotaInfo> {
  const response = await apiFetch(`${API_BASE}/api/tenant/quota`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load quota');
  return data as TenantQuotaInfo;
}

// Token Usage Tree types
export interface TokenUsageTreeOverview {
  totalTokens: number;
  realplcOutputTokens?: number;
  activeUsers: number;
  activeSessions: number;
  avgPerUser: number;
  prevTotalTokens?: number;
  prevActiveUsers?: number;
  prevActiveSessions?: number;
  prevAvgPerUser?: number;
}

export interface TokenUsageConsistency {
  aligned: boolean;
  overviewTotal: number;
  totals: Record<string, number>;
  differences: Record<string, number>;
}

export interface TokenUsageTrendPoint {
  date: string;
  tokens: number;
  realplcOutputTokens?: number;
  prevTokens?: number;
}

export interface TokenUsageModelBreakdown {
  model: string;
  provider?: string;
  finalModel?: string;
  tokens: number;
  realplcOutputTokens?: number;
  percentage: number;
}

export interface TokenUsageOrgNode {
  orgId: string;
  orgName: string;
  tokens: number;
  realplcOutputTokens?: number;
  userCount: number;
  users: TokenUsageUserNode[];
}

export interface TokenUsageUserNode {
  userId: string;
  userName: string;
  tokens: number;
  realplcOutputTokens?: number;
  sessionCount: number;
}

export interface TokenUsageTreeResponse {
  dateRange?: { startMs: number; endMs: number };
  overview: TokenUsageTreeOverview;
  trend: TokenUsageTrendPoint[];
  modelBreakdown: TokenUsageModelBreakdown[];
  orgTree: TokenUsageOrgNode[];
  includeRealplcOutput?: boolean;
  consistency?: TokenUsageConsistency;
}

export interface TokenUsageSessionEntry {
  sessionId: string;
  sessionTitle: string;
  deletedAt?: number | null;
  startedAt: string;
  model: string;
  modelBreakdown?: Array<{ provider: string; model: string; label: string; tokens: number; realplcOutputTokens?: number; messageCount: number }>;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  realplcOutputTokens?: number;
  messageCount: number;
}

export interface TokenUsageSessionsResponse {
  userId: string;
  userName: string;
  orgId: string;
  orgName: string;
  totalTokens: number;
  realplcOutputTokens?: number;
  sessions: TokenUsageSessionEntry[];
  pagination: { offset: number; limit: number; total: number };
}

// Admin User Management types
/** Window usage attached to each row of the admin user list (30 days). */
export interface AdminUserUsage {
  /** users.id — what the session list filters on; null when the account never signed in. */
  internalUserId: string | null;
  sessions: number;
  turns: number;
  failedTurns: number;
  totalTokens: number;
  lastActiveAt: number | null;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  realmRoles: string[];
  createdAt: number;
  lastLoginAt?: number | null;
  usage?: AdminUserUsage | null;
}

export interface AdminUserListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminUserListResult {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateUserPayload {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  realmRoles: string[];
}

export interface EmailTemplate {
  id: string;
  name: string;
  code: string;
  subject: string;
  body: string;
  vars: string[];
  updated_at: number;
}

// Admin User Management API functions
export async function createAdminUser(payload: CreateUserPayload): Promise<AdminUser & { emailWarning?: string; emailSent?: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '创建用户失败');
  }
  return data as AdminUser & { emailWarning?: string; emailSent?: boolean };
}

export async function listAdminUsers(params: AdminUserListParams = {}): Promise<AdminUserListResult> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  qs.set('page', String(params.page ?? 0));
  qs.set('pageSize', String(params.pageSize ?? 20));
  const response = await apiFetch(`${API_BASE}/api/admin/users?${qs.toString()}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '获取用户列表失败');
  }
  return data as AdminUserListResult;
}

export async function updateAdminUser(id: string, patch: Partial<Pick<CreateUserPayload, 'email' | 'firstName' | 'lastName' | 'realmRoles'>>): Promise<{ ok: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '更新用户失败');
  }
  return data as { ok: boolean };
}

export async function updateAdminUserEnabled(id: string, enabled: boolean): Promise<{ ok: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}/enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '操作失败');
  }
  return data as { ok: boolean };
}

export async function resendAdminPassword(userId: string): Promise<{ ok: boolean; emailWarning?: string }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/resend-password`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '重发密码失败');
  }
  return data as { ok: boolean; emailWarning?: string };
}

export interface AdminPriorityData {
  distribution: Record<string, {
    userCount: number;
    avgScore: number;
    waiting: number;
  }>;
  topUsers: Array<{
    rank: number;
    userId: string;
    email: string;
    planTier: string;
    behaviorScore: number;
    monthlyTokens: number;
    priorityScore: number;
    trendVsLastWeek: 'up' | 'down' | 'stable';
  }>;
  queueStatus: {
    waiting: number;
    active: number;
    completed24h: number;
    failed24h: number;
    avgWaitTimeMs: number;
    waitingByTier: Record<string, number>;
    trendVsLastHour: {
      waiting: number;
      avgWaitTimeMs: number;
    };
  };
}

export async function loadPriorityOverview(): Promise<AdminPriorityData> {
  const response = await apiFetch(`${API_BASE}/api/admin/priority-overview`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load priority overview');
  return data as AdminPriorityData;
}

export async function loadAiProviderPools(): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load AI provider pools');
  return data.providers as AiProviderPool[];
}

export interface EnvOnlyProvider {
  provider: string;
  displayName: string;
  baseUrl: string;
  envVar: string;
  keyFingerprint: string;
}

export interface OrphanPoolModel {
  provider: string;
  models: number;
  poolTypes: Array<{ poolType: string; models: number }>;
}

export interface StaleImportedKey {
  provider: string;
  keyId: string;
  keyName: string;
  envVar: string;
}

export interface UnusedProviderKey {
  provider: string;
  keyId: string;
  keyName: string;
  ageDays: number;
  poolModels: number;
}

export interface AiProviderDiagnostics {
  envOnlyProviders: EnvOnlyProvider[];
  orphanPoolModels: OrphanPoolModel[];
  staleImportedKeys: StaleImportedKey[];
  unusedProviderKeys: UnusedProviderKey[];
}

export async function loadAiProviderDiagnostics(): Promise<AiProviderDiagnostics> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers/diagnostics`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load provider diagnostics');
  return data as AiProviderDiagnostics;
}

export interface ScorecardRun {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  poolTypes: string[];
  scenarioCount: number;
  scenarioSet: ModelScorecardScenarioSet | null;
  gateScore: number;
  totalTasks: number;
  completedTasks: number;
  errorMessage: string | null;
  appliedAt: number | null;
  startedAt: number | null;
  finishedAt: number | null;
  createdAt: number;
}

export interface ScorecardRanking {
  poolType: string;
  provider: string;
  model: string;
  meanScore: number;
  medianLatencyMs: number | null;
  scored: number;
  failed: number;
  passesGate: boolean;
  priority: number;
}

export async function loadScorecardEstimate(
  params: { poolTypes?: string[]; scenarioCount?: number; scenarioSet?: ModelScorecardScenarioSet } = {},
) {
  const query = new URLSearchParams();
  if (params.poolTypes?.length) query.set('poolTypes', params.poolTypes.join(','));
  if (params.scenarioCount) query.set('scenarioCount', String(params.scenarioCount));
  if (params.scenarioSet) query.set('scenarioSet', params.scenarioSet);
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/estimate?${query.toString()}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to estimate scorecard run');
  return data as {
    poolTypes: string[]; scenarioSet: ModelScorecardScenarioSet; scenarioCount: number;
    models: number; generations: number;
  };
}

export async function startScorecardRun(
  body: { poolTypes?: string[]; scenarioCount?: number; scenarioSet?: ModelScorecardScenarioSet; gateScore?: number },
) {
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to start scorecard run');
  return data as { id: string };
}

export async function loadScorecardRuns(): Promise<ScorecardRun[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/runs`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load scorecard runs');
  return data.runs as ScorecardRun[];
}

export async function loadScorecardRun(runId: string): Promise<{ run: ScorecardRun; rankings: ScorecardRanking[] }> {
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/runs/${encodeURIComponent(runId)}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load scorecard run');
  return data as { run: ScorecardRun; rankings: ScorecardRanking[] };
}

export async function applyScorecardRanking(runId: string): Promise<{ applied: number }> {
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/runs/${encodeURIComponent(runId)}/apply`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to apply ranking');
  return data as { applied: number };
}

export async function cancelScorecardRun(runId: string): Promise<{ id: string; status: 'cancelled' }> {
  const response = await apiFetch(`${API_BASE}/api/admin/model-scorecard/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to cancel scorecard run');
  return data as { id: string; status: 'cancelled' };
}

export interface ImportFromEnvResult {
  count: number;
  skipped: Array<{ provider: string; reason: 'unknown_provider' | 'not_configured' | 'already_exists' }>;
  providers: AiProviderPool[];
}

export async function importAiProvidersFromEnv(providers: string[]): Promise<ImportFromEnvResult> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers/import-from-env`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providers }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to import providers from environment');
  return result as ImportFromEnvResult;
}

export async function createAiProvider(data: {
  name: string;
  provider: string;
  baseUrl?: string;
  priority?: number;
}): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to create AI provider');
  return result.providers as AiProviderPool[];
}

export async function updateAiProvider(id: string, data: Partial<{
  name: string;
  provider: string;
  baseUrl: string | null;
  status: string;
  priority: number;
}>): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to update AI provider');
  return result.providers as AiProviderPool[];
}

/**
 * @param force required by the server once the provider still holds keys,
 *   since deleting it also destroys those keys and their usage history.
 */
export async function deleteAiProvider(id: string, options: { force?: boolean } = {}): Promise<AiProviderPool[]> {
  const query = options.force ? '?force=true' : '';
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers/${encodeURIComponent(id)}${query}`, {
    method: 'DELETE',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to delete AI provider');
  return result.providers as AiProviderPool[];
}

export async function createAiProviderKey(providerId: string, data: {
  name?: string;
  apiKey: string;
  maxConcurrency?: number;
  rpmLimit?: number | null;
  dailyLimit?: number | null;
}): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-providers/${encodeURIComponent(providerId)}/keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to create AI provider key');
  return result.providers as AiProviderPool[];
}

export async function updateAiProviderKey(keyId: string, data: Partial<{
  name: string;
  status: string;
  maxConcurrency: number;
  rpmLimit: number | null;
  dailyLimit: number | null;
  apiKey: string;
  resetCooldown: boolean;
}>): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-provider-keys/${encodeURIComponent(keyId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to update AI provider key');
  return result.providers as AiProviderPool[];
}

export async function deleteAiProviderKey(keyId: string): Promise<AiProviderPool[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/ai-provider-keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to delete AI provider key');
  return result.providers as AiProviderPool[];
}

export async function loadEmailTemplates(): Promise<EmailTemplate[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/email-templates`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || '获取邮件模板列表失败');
  return data.templates as EmailTemplate[];
}

export async function getEmailTemplate(code: string): Promise<EmailTemplate> {
  const response = await apiFetch(`${API_BASE}/api/admin/email-templates/${encodeURIComponent(code)}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || '获取邮件模板失败');
  return data.template as EmailTemplate;
}

export async function saveEmailTemplate(code: string, data: { name: string; subject: string; body: string; vars?: string[] }): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/admin/email-templates/${encodeURIComponent(code)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || '保存邮件模板失败');
  }
}

export async function uploadEmailImage(filename: string, base64Data: string): Promise<string> {
  const response = await apiFetch(`${API_BASE}/api/admin/email-templates/upload-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, data: base64Data }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || '上传图片失败');
  return result.url as string;
}

// Bulk user creation types
export interface BulkUserEntry {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  realmRoles?: string[];
}

export interface BulkUserResult {
  userId?: string;
  username: string;
  email: string;
  status: 'ok' | 'duplicate' | 'fail';
  error?: string;
  password?: string;
  name?: string;
  initialEmailQueueId?: string;
  initialEmailStatus?: 'not_sent' | 'queued' | 'sending' | 'sent' | 'failed';
  initialEmailError?: string;
}

export interface BulkCreateResponse {
  summary: { created: number; duplicate: number; failed: number; total: number };
  results: BulkUserResult[];
}

export interface BulkSendEmailEntry {
  id?: string;
  templateCode: string;
  to: string;
  variables: Record<string, string>;
}

export interface BulkEmailQueueItem {
  id: string;
  templateCode: string;
  to: string;
  status: 'queued' | 'sending' | 'sent' | 'failed';
  error?: string;
  updatedAt: number;
  batchId?: string;
}

export interface EmailQueueStatus {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  currentEmail?: string;
  running: boolean;
  items: BulkEmailQueueItem[];
  etaMs: number;
  intervalMs: number;
}

export interface SystemSettings {
  emailIntervalMs: number;
  autopoolForcePoolOrder: boolean;
  streamInitialResponseTimeoutMs: number;
  modelGoneDisableThreshold: number;
  runtimeReloaded?: boolean;
  visionModelConfig?: {
    enabled: boolean;
    provider: string;
    model: string;
    maxImages: number;
    maxTokens: number;
    timeout: number;
  } | null;
  visionModelProvider?: string;
  visionModelName?: string;
  appVersion?: string;
  appReleaseNotes?: string;
}

export interface PublicVersion {
  version: string;
  releaseNotes: string;
}

export async function fetchPublicVersion(): Promise<PublicVersion> {
  const response = await fetch(`${API_BASE}/api/public/version`);
  const data = await response.json();
  return data as PublicVersion;
}

export async function createAdminUsersBulk(users: BulkUserEntry[]): Promise<BulkCreateResponse> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ users }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '批量创建用户失败');
  }
  return data as BulkCreateResponse;
}

export async function sendBulkEmails(emails: BulkSendEmailEntry[]): Promise<{ queued: number; intervalMs: number; items: BulkEmailQueueItem[]; batchId: string }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/bulk/send-emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '发送邮件失败');
  }
  return data;
}

export async function getBulkEmailStatus(batchId?: string): Promise<EmailQueueStatus> {
  const params = batchId ? `?batchId=${encodeURIComponent(batchId)}` : '';
  const response = await apiFetch(`${API_BASE}/api/admin/users/bulk/email-status${params}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || '获取邮件队列状态失败');
  }
  return data as EmailQueueStatus;
}

export async function getSystemSettings(): Promise<SystemSettings> {
  const response = await apiFetch(`${API_BASE}/api/admin/settings`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || '获取系统设置失败');
  }
  return data as SystemSettings;
}

export async function updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
  const response = await apiFetch(`${API_BASE}/api/admin/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || '更新系统设置失败');
  }
  return data as SystemSettings;
}

export async function updateVisionModelConfig(config: NonNullable<SystemSettings['visionModelConfig']>): Promise<{
  success: boolean;
  config: NonNullable<SystemSettings['visionModelConfig']>;
  runtimeReloaded: boolean;
}> {
  const response = await apiFetch(`${API_BASE}/api/admin/settings/vision`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to update vision model config');
  }
  return data as {
    success: boolean;
    config: NonNullable<SystemSettings['visionModelConfig']>;
    runtimeReloaded: boolean;
  };
}

export async function sendAdminEmail(
  userId: string,
  data: { subject: string; body: string },
): Promise<{ ok: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || result.error || '发送邮件失败');
  }
  return result as { ok: boolean };
}

// ---------------------------------------------------------------------------
// Autopool Models
// ---------------------------------------------------------------------------

export interface AutopoolModel {
  id: string;
  poolType: string;
  provider: string;
  model: string;
  label?: string | null;
  /** Task classes the model is preferred for; null means every task. */
  tasks: string[] | null;
  status: string;
  priority: number;
  createdAt: number;
  updatedAt: number;
}

export async function loadAutopoolModels(): Promise<AutopoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load autopool models');
  return data.models as AutopoolModel[];
}

export async function createAutopoolModel(body: {
  poolType: string;
  provider: string;
  model: string;
  label?: string;
  tasks?: string[];
  priority?: number;
}): Promise<AutopoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to create autopool model');
  return result.models as AutopoolModel[];
}

export async function updateAutopoolModel(id: string, body: Partial<{
  poolType: string;
  provider: string;
  model: string;
  label: string | null;
  tasks: string[] | null;
  status: string;
  priority: number;
}>): Promise<AutopoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to update autopool model');
  return result.models as AutopoolModel[];
}

export async function reorderAutopoolModels(poolType: string, orderedIds: string[]): Promise<AutopoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poolType, orderedIds }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to reorder autopool models');
  return result.models as AutopoolModel[];
}

export async function deleteAutopoolModel(id: string): Promise<AutopoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to delete autopool model');
  return result.models as AutopoolModel[];
}

// ── Vision pool ──
export interface VisionPoolModel {
  id: string;
  poolType: string;
  provider: string;
  model: string;
  label: string | null;
  status: string;
  priority: number;
  baseUrl: string | null;
  providerName: string | null;
  keyCount: number;
}

export async function loadVisionPool(): Promise<VisionPoolModel[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/vision-pool`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load vision pool');
  return data.models as VisionPoolModel[];
}

export async function syncVisionPool(): Promise<{ success: boolean; runtimeReloaded: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/vision-pool/sync`, { method: 'POST' });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to sync vision pool');
  return data as { success: boolean; runtimeReloaded: boolean };
}

// Add vision model to pool (reuses autopool-models endpoint with poolType='vision')
export async function createVisionPoolModel(body: {
  provider: string;
  model: string;
  label?: string;
  priority?: number;
}): Promise<AutopoolModel[]> {
  return createAutopoolModel({ ...body, poolType: 'vision' });
}

export async function reloadAutopoolModels(): Promise<{ models: AutopoolModel[]; reloadedAt: number; runtimeReloaded: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/reload`, {
    method: 'POST',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to reload autopool models');
  return result as { models: AutopoolModel[]; reloadedAt: number; runtimeReloaded: boolean };
}

// ---- User-facing model catalog (what the chat composer offers) ----

/** A model as a provider's list or models.dev describes it. */
export interface ChatCatalogListedModel {
  id: string;
  name?: string;
  releaseDate?: string;
  contextWindow?: number;
  reasoning?: boolean;
  vision?: boolean;
  toolCall?: boolean;
  beta?: boolean;
}

export type ChatCatalogDiscoveryErrorCode =
  | 'no_source' | 'no_base_url' | 'official_auth' | 'official_not_found' | 'official_unavailable'
  | 'registry_unavailable' | 'registry_missing' | 'empty_list' | 'internal';

export interface ChatCatalogLastCheck {
  at: number;
  /** 'official', 'none' or 'registry:<models.dev provider id>'. */
  source: string | null;
  ok: boolean;
  errorCode?: ChatCatalogDiscoveryErrorCode;
  detail?: string;
  added?: number;
  baseline?: boolean;
  /** Catalog models the last successful list did not carry. */
  missing: string[];
}

export interface ChatCatalogProviderDiscovery {
  autoDiscover: boolean;
  /** 'auto', 'official', 'none' or 'registry:<id>'. */
  sourceSetting: string;
  resolvedSource: string | null;
  lastCheck: ChatCatalogLastCheck | null;
}

export interface ChatCatalogProviderRow {
  id: string;
  provider: string;
  label: string;
  baseUrl: string | null;
  visible: boolean;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
  discovery?: ChatCatalogProviderDiscovery;
}

export interface ChatCatalogPendingModel {
  id: string;
  provider: string;
  model: string;
  source: string;
  name: string | null;
  meta: Omit<ChatCatalogListedModel, 'id' | 'name'>;
  firstSeenAt: number;
  lastSeenAt: number;
}

export interface ChatCatalogCheckOutcome {
  providerId?: string;
  provider: string;
  source?: string | null;
  ok: boolean;
  errorCode?: ChatCatalogDiscoveryErrorCode;
  detail?: string;
  added: number;
  baseline: boolean;
  missing: number;
}

export interface ChatCatalogRegistryProvider {
  id: string;
  name: string;
  api: string | null;
  models: number;
}

export interface ChatCatalogModelRow {
  id: string;
  provider: string;
  model: string;
  label: string | null;
  visible: boolean;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export interface ChatCatalogAdminData {
  providers: ChatCatalogProviderRow[];
  models: ChatCatalogModelRow[];
  /** Suggestions from discovery waiting for a decision. */
  pending?: ChatCatalogPendingModel[];
}

export interface ChatCatalogSeedResult extends ChatCatalogAdminData {
  inserted: { providers: number; models: number };
  skipped: { providers: number; models: number };
}

/** Failures carry the server's `code` when it sends one, so the panel can say why in the user's language. */
async function chatCatalogRequest<T>(path: string, init: RequestInit | undefined, failure: string): Promise<T> {
  const response = await apiFetch(`${API_BASE}/api/admin/chat-catalog${path}`, init);
  const data = await response.json();
  if (!response.ok) throw Object.assign(new Error(data.message || data.error || failure), { code: data.code });
  return data as T;
}

function jsonBody(method: 'POST' | 'PATCH', body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

export async function loadChatCatalog(): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest('', undefined, 'Failed to load the model catalog');
}

export async function createChatCatalogProvider(body: { provider: string; label: string; baseUrl?: string; visible?: boolean; sortOrder?: number }): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest('/providers', jsonBody('POST', body), 'Failed to add provider');
}

export async function updateChatCatalogProvider(id: string, body: {
  label?: string;
  baseUrl?: string | null;
  visible?: boolean;
  sortOrder?: number;
  autoDiscover?: boolean;
  discoverySource?: string;
}): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest(`/providers/${encodeURIComponent(id)}`, jsonBody('PATCH', body), 'Failed to update provider');
}

export async function deleteChatCatalogProvider(id: string): Promise<ChatCatalogAdminData & { deletedModels: number }> {
  return chatCatalogRequest(`/providers/${encodeURIComponent(id)}`, { method: 'DELETE' }, 'Failed to delete provider');
}

export async function createChatCatalogModel(body: { provider: string; model: string; label?: string; visible?: boolean; sortOrder?: number }): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest('/models', jsonBody('POST', body), 'Failed to add model');
}

export async function updateChatCatalogModel(id: string, body: { label?: string; visible?: boolean; sortOrder?: number }): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest(`/models/${encodeURIComponent(id)}`, jsonBody('PATCH', body), 'Failed to update model');
}

export async function deleteChatCatalogModel(id: string): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest(`/models/${encodeURIComponent(id)}`, { method: 'DELETE' }, 'Failed to delete model');
}

export async function seedChatCatalog(body: {
  providers: Array<{ provider: string; label: string; baseUrl: string | null; visible: boolean; sortOrder: number }>;
  models: Array<{ provider: string; model: string; label: string | null; visible: boolean; sortOrder: number }>;
}): Promise<ChatCatalogSeedResult> {
  return chatCatalogRequest('/seed', jsonBody('POST', body), 'Failed to import the catalog');
}

export async function checkChatCatalogProvider(id: string): Promise<ChatCatalogAdminData & { check: ChatCatalogCheckOutcome }> {
  return chatCatalogRequest(`/providers/${encodeURIComponent(id)}/check`, jsonBody('POST', {}), 'Failed to check the provider');
}

export async function checkAllChatCatalogProviders(): Promise<ChatCatalogAdminData & { checks: ChatCatalogCheckOutcome[] }> {
  return chatCatalogRequest('/check-all', jsonBody('POST', {}), 'Failed to check the providers');
}

/** The provider's whole model list, keyless: its own API when public, models.dev otherwise. */
export async function listChatCatalogProviderModels(id: string): Promise<{ source: string; models: ChatCatalogListedModel[] }> {
  return chatCatalogRequest(`/providers/${encodeURIComponent(id)}/list-models`, jsonBody('POST', {}), 'Failed to list the models');
}

export async function approveChatCatalogDiscovery(id: string): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest(`/discoveries/${encodeURIComponent(id)}/approve`, jsonBody('POST', {}), 'Failed to approve the model');
}

export async function dismissChatCatalogDiscovery(id: string): Promise<ChatCatalogAdminData> {
  return chatCatalogRequest(`/discoveries/${encodeURIComponent(id)}/dismiss`, jsonBody('POST', {}), 'Failed to dismiss the model');
}

export async function decideChatCatalogDiscoveries(body: { action: 'approve' | 'dismiss'; provider?: string }): Promise<ChatCatalogAdminData & { decided: number }> {
  return chatCatalogRequest('/discoveries/decide', jsonBody('POST', body), 'Failed to decide the suggestions');
}

export async function loadChatCatalogPendingCount(): Promise<number> {
  const data = await chatCatalogRequest<{ pending: number }>('/discoveries/count', undefined, 'Failed to count suggestions');
  return data.pending;
}

export async function loadChatCatalogRegistryProviders(): Promise<ChatCatalogRegistryProvider[]> {
  const data = await chatCatalogRequest<{ providers: ChatCatalogRegistryProvider[] }>('/registry-providers', undefined, 'Failed to read models.dev');
  return data.providers;
}

export async function seedAutopoolModels(): Promise<{ models: AutopoolModel[]; count: number; skipped?: number }> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/seed`, {
    method: 'POST',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || 'Failed to seed autopool models');
  return result as { models: AutopoolModel[]; count: number; skipped?: number };
}

export interface AutopoolModelStats {
  provider: string;
  model: string;
  successCount: number;
  failureCount: number;
  avgLatencyMs: number | null;
  lastError: string | null;
  lastUsedAt: number | null;
}

export async function loadAutopoolModelStats(): Promise<AutopoolModelStats[]> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/stats`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load autopool model stats');
  return data.stats as AutopoolModelStats[];
}

/** What ai-runtime currently believes about one pool model. */
export interface AutopoolModelHealth {
  usable?: boolean;
  configuredKeys?: number;
  availableKeys?: number;
  exclusionReasons?: string[];
  lastSuccessfulAt?: number | null;
  provider: string;
  model: string;
  status: 'available' | 'cooling' | 'auth_failed' | 'model_gone' | 'rate_limited' | 'degraded';
  /** Epoch ms; null when not cooling. Number.MAX_SAFE_INTEGER means for the life of the runtime process. */
  cooldownUntil: number | null;
  reason: string | null;
  observedLatencyMs: number | null;
  failureScore: number;
  consecutiveFailures: number;
}

export interface AutopoolHealthSnapshot {
  updatedAt: number | null;
  models: AutopoolModelHealth[];
  providers: Array<{ provider: string; recentFailures: number }>;
}

export async function loadAutopoolModelHealth(): Promise<AutopoolHealthSnapshot> {
  const response = await apiFetch(`${API_BASE}/api/admin/autopool-models/health`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to load autopool model health');
  return data as AutopoolHealthSnapshot;
}

// ---------------------------------------------------------------------------
// Provider Model Discovery
// ---------------------------------------------------------------------------

export interface FetchedModel {
  id: string;
  owned_by: string;
  created: number;
}

export interface FetchModelsResult {
  models: FetchedModel[];
  total: number;
  provider: string;
  fetchedAt: number;
}

export interface TestModelResult {
  provider: string;
  model: string;
  status: 'ok' | 'error';
  ttftMs: number;
  totalMs: number;
  finishReason?: string;
  error?: string;
  testedAt?: number;
}

export interface TestAllModelsResult {
  results: TestModelResult[];
  provider: string;
  testedAt: number;
}

async function parseJsonResponse(response: Response, label: string) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text().catch(() => '');
    throw new Error(`${label}: server returned non-JSON response (${response.status})${text ? ': ' + text.slice(0, 200) : ''}`);
  }
  return response.json();
}

export async function fetchProviderModels(provider: string): Promise<FetchModelsResult> {
  const response = await apiFetch(`${API_BASE}/api/admin/provider/fetch-models`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider }),
  });
  const data = await parseJsonResponse(response, 'fetchProviderModels');
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to fetch provider models');
  return data as FetchModelsResult;
}

export async function testProviderModel(provider: string, model: string, mode: 'chat' | 'vision' = 'chat'): Promise<TestModelResult> {
  const response = await apiFetch(`${API_BASE}/api/admin/provider/test-model`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, model, mode }),
  });
  const data = await parseJsonResponse(response, 'testProviderModel');
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to test model');
  return data as TestModelResult;
}

export async function testAllProviderModels(provider: string, models: string[], mode: 'chat' | 'vision' = 'chat'): Promise<TestAllModelsResult> {
  const response = await apiFetch(`${API_BASE}/api/admin/provider/test-all-models`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, models, mode }),
  });
  const data = await parseJsonResponse(response, 'testAllProviderModels');
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to test models');
  return data as TestAllModelsResult;
}

// ---- Plan entitlements ----

export type PlanTierName = 'free' | 'pro' | 'max' | 'business';

export interface EntitlementKeyInfo {
  key: string;
  kind: string;
  defaults: Record<PlanTierName, number>;
}

export interface EntitlementTemplates {
  tiers: PlanTierName[];
  keys: EntitlementKeyInfo[];
  /** Only the values a super admin actually set; anything missing falls back to `defaults`. */
  templates: Record<string, Record<string, number>>;
}

export interface OrgEntitlementEntry {
  key: string;
  value: number;
  source: 'registry' | 'template' | 'override';
  reason: string | null;
}

export interface OrgEntitlements {
  organizationId: string;
  planTier: PlanTierName;
  entries: OrgEntitlementEntry[];
}

async function entitlementsRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(`${API_BASE}/api/admin/entitlements${path}`, init);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || '套餐权益操作失败');
  return data as T;
}

export function loadEntitlementTemplates(): Promise<EntitlementTemplates> {
  return entitlementsRequest<EntitlementTemplates>('');
}

/** A `null` value clears the row so the tier falls back to the registry default. */
export function saveEntitlementTemplate(
  planTier: PlanTierName,
  entitlements: Record<string, number | null>,
): Promise<{ ok: boolean; templates: Record<string, Record<string, number>> }> {
  return entitlementsRequest('', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planTier, entitlements }),
  });
}

export function loadOrgEntitlements(organizationId: string): Promise<OrgEntitlements> {
  return entitlementsRequest<OrgEntitlements>(`/organizations/${encodeURIComponent(organizationId)}`);
}

export function saveOrgEntitlementOverride(
  organizationId: string,
  entitlements: Record<string, number>,
  reason: string,
): Promise<{ ok: boolean }> {
  return entitlementsRequest(`/organizations/${encodeURIComponent(organizationId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entitlements, reason }),
  });
}

export function deleteOrgEntitlementOverride(
  organizationId: string,
  key: string,
): Promise<{ ok: boolean }> {
  return entitlementsRequest(
    `/organizations/${encodeURIComponent(organizationId)}/${encodeURIComponent(key)}`,
    { method: 'DELETE' },
  );
}
